import { ApiResponse, User } from '../types';
import { apiClient } from './apiClient';

interface LoginResponse {
  user: User;
  token: string;
}

class AuthService {
  async login(credentials: { email: string; password: string }): Promise<LoginResponse> {
    const response = await apiClient.post('/api/rbac/login', credentials);
    // Server returns data directly, not wrapped in ApiResponse
    if (response.data && response.data.user && response.data.token) {
      return {
        user: {
          ...response.data.user,
          role: response.data.user.roleId, // Map roleId to role for compatibility
        },
        token: response.data.token,
      };
    }
    throw new Error(response.data?.error || 'Login failed');
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      // Even if logout fails on server, we clear local state
      console.warn('Logout request failed:', error);
    } finally {
      localStorage.removeItem('dns-fabric-token');
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<ApiResponse<User>>('/api/auth/me');
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get current user');
  }

  async refreshToken(): Promise<string> {
    const response = await apiClient.post<ApiResponse<{ token: string }>>('/api/auth/refresh');
    if (response.data.data) {
      const newToken = response.data.data.token;
      localStorage.setItem('dns-fabric-token', newToken);
      return newToken;
    }
    throw new Error('Failed to refresh token');
  }

  getToken(): string | null {
    return localStorage.getItem('dns-fabric-token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired (basic check)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService();
